export enum FileSizeUnit {
  Kilobytes = 'Kilobytes',
  Megabytes = 'Megabytes',
}

const FileSizeUnitModels = {
  [FileSizeUnit.Kilobytes]: { abbreviatedLabel: 'KB', label: 'Kilobytes', multiplier: 1024 },
  [FileSizeUnit.Megabytes]: { abbreviatedLabel: 'MB', label: 'Megabytes', multiplier: 1024 * 1024 },
} as const satisfies Record<
  FileSizeUnit,
  { readonly abbreviatedLabel: string; readonly label: string; readonly multiplier: number }
>;

export type FileSizeUnitModel<I extends FileSizeUnit> = (typeof FileSizeUnitModels)[I];

export const getFileSizeUnitModel = <I extends FileSizeUnit>(unit: I): FileSizeUnitModel<I> =>
  FileSizeUnitModels[unit];

export type FileSize<I extends FileSizeUnit = FileSizeUnit> = I extends FileSizeUnit
  ? { readonly unit: I; readonly value: number }
  : never;

export const parseFileSizeBytes = (size: FileSize): number =>
  getFileSizeUnitModel(size.unit).multiplier * size.value;

interface StringifyFileSizeOptions {
  readonly abbreviated?: boolean;
  readonly formatter?: <U extends FileSizeUnit>(params: {
    readonly abbreviatedLabel: FileSizeUnitModel<U>['abbreviatedLabel'];
    readonly label: FileSizeUnitModel<U>['label'];
    readonly unit: U;
    readonly value: number;
  }) => string;
}

export const stringifyFileSize = (size: FileSize, options?: StringifyFileSizeOptions): string => {
  if (options?.formatter) {
    return options.formatter({
      abbreviatedLabel: getFileSizeUnitModel(size.unit).abbreviatedLabel,
      label: getFileSizeUnitModel(size.unit).label,
      unit: size.unit,
      value: size.value,
    });
  }
  const label = options?.abbreviated
    ? getFileSizeUnitModel(size.unit).abbreviatedLabel
    : getFileSizeUnitModel(size.unit).label;
  return `${size.value}${label}`;
};
